/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { ICellRendererParams } from 'ag-grid-community'
import colors from '@sportninja/common/constants/appColors'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import {
  isDraftTeam,
  startTeamImageUrl,
} from '@sportninja/common/features/registration/startStep/teamDisplay'
import { BUCKET_IMAGE_SRC } from 'src/components/ts-components/icons/bucketImage'
import type { TeamRow } from '../utils/entityGridColumns'

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

/**
 * The team's crest beside its name, so a row is recognisable at a glance the
 * way its card is.
 *
 * The column keeps its `valueGetter`, so sorting and the quick filter still run
 * on the plain name — this renderer only changes how that name is drawn.
 */
const TeamNameCell = ({ data }: ICellRendererParams<TeamRow>) => {
  'use memo'

  //  AG Grid renders this cell for its loading and full-width rows with no data.
  if (data === undefined) {
    return null
  }

  const team = data.team
  //  The same three-way the cards use: a draft has no crest of its own and takes
  //  the shared bucket image, a team without one falls back to the placeholder,
  //  and a real image is resolved to the active CDN host.
  const logo = isDraftTeam(team)
    ? BUCKET_IMAGE_SRC
    : resolveAssetUrl(startTeamImageUrl(team)) || TEAM_PLACEHOLDER

  return (
    <span css={wrapStyle}>
      <img css={logoStyle} src={logo} alt='' />
      <span css={nameStyle}>{team?.name ?? 'Unnamed Team'}</span>
    </span>
  )
}

export default TeamNameCell

/* Fills the cell so the row height, not the content, sets the alignment. */
const wrapStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  height: 100%;
`

/* Sized to the row rather than to the type, with the same 8px radius and
   NEUTRAL_700 well the team cards give their crests. */
const logoStyle = css`
  width: 24px;
  height: 24px;
  flex: none;
  border-radius: 8px;
  object-fit: contain;
  background-color: ${colors.NEUTRAL_700};
`

/* The name truncates rather than pushing the logo out of the cell. */
const nameStyle = css`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
